import { useState } from 'react';
import { useAuth } from '@/features/auth/AuthContext';
import type { Entity } from '@/features/auth/AuthContext';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { TransactionsPage } from '@/features/transactions/TransactionsPage';
import { AccountsPage } from '@/features/accounts/AccountsPage';
import { CardsPage } from '@/features/cards/CardsPage';
import { TransfersPage } from '@/features/transfers/TransfersPage';
import { RecurrencesPage } from '@/features/recurrences/RecurrencesPage';
import { ObligationsPage } from '@/features/obligations/ObligationsPage';
import { PartiesPage } from '@/features/parties/PartiesPage';
import { ReportsPage } from '@/features/reports/ReportsPage';
import { ForecastPage } from '@/features/forecast/ForecastPage';
import { DebtsPage } from '@/features/debts/DebtsPage';
import { BudgetPage } from '@/features/budget/BudgetPage';
import { CategoriesPage } from '@/features/settings/CategoriesPage';
import { EntitiesPage } from '@/features/settings/EntitiesPage';
import { AuditPage } from '@/features/settings/AuditPage';
import { UsersPage } from '@/features/settings/UsersPage';
import { UpdatesPage } from '@/features/settings/UpdatesPage';
import { SharingPage } from '@/features/sharing/SharingPage';
import { BackupPage } from '@/features/settings/BackupPage';
import { WhatsAppPage } from '@/features/settings/WhatsAppPage';
import { AlertsPage } from '@/features/alerts/AlertsPage';
import { ContractsPage } from '@/features/contracts/ContractsPage';
import { NotificationBell } from '@/features/alerts/NotificationBell';
import { PeriodSelector } from './PeriodSelector';
import { ThemeToggle } from './ThemeToggle';
import { ServerClock } from './ServerClock';
import { useDismiss } from './useDismiss';

/**
 * Casca da aplicacao: barra superior com seletor de entidade, menu lateral e
 * area de conteudo.
 *
 * O SELETOR DE ENTIDADE e o controle mais importante da interface. Em um sistema
 * multi-entidade, o usuario nunca pode se perguntar "de quem e este numero" —
 * por isso o contexto fica permanentemente visivel, nao escondido em um menu.
 */

type View =
  | 'dashboard' | 'pagar' | 'receber' | 'lancamentos' | 'recorrencias'
  | 'transferencias' | 'contas' | 'cartoes' | 'dividas' | 'orcamento'
  | 'contatos' | 'previsao' | 'relatorios' | 'categorias' | 'entidades'
  | 'auditoria' | 'compartilhadas' | 'backup' | 'alertas' | 'contratos' | 'usuarios' | 'atualizacoes'
  | 'whatsapp';

const MENU: Array<{ id: View; label: string; icon: string; ready: boolean }> = [
  { id: 'dashboard', label: 'Dashboard', icon: '◫', ready: true },
  { id: 'pagar', label: 'A pagar', icon: '↑', ready: true },
  { id: 'receber', label: 'A receber', icon: '↓', ready: true },
  { id: 'previsao', label: 'Previsão de caixa', icon: '◷', ready: true },
  { id: 'orcamento', label: 'Orçamento', icon: '◱', ready: true },
  { id: 'lancamentos', label: 'Lançamentos', icon: '≡', ready: true },
  { id: 'transferencias', label: 'Transferências', icon: '⇄', ready: true },
  { id: 'recorrencias', label: 'Recorrências', icon: '↻', ready: true },
  { id: 'contratos', label: 'Contratos', icon: '§', ready: true },
  { id: 'contas', label: 'Contas', icon: '▤', ready: true },
  { id: 'cartoes', label: 'Cartões', icon: '▭', ready: true },
  { id: 'dividas', label: 'Dívidas e CET', icon: '◈', ready: true },
  { id: 'compartilhadas', label: 'Acerto do casal', icon: '⇌', ready: true },
  { id: 'contatos', label: 'Clientes e fornecedores', icon: '☺', ready: true },
  { id: 'relatorios', label: 'Relatórios', icon: '◲', ready: true },
  { id: 'categorias', label: 'Plano de contas', icon: '☰', ready: true },
  { id: 'entidades', label: 'Entidades', icon: '⌂', ready: true },
  { id: 'alertas', label: 'Alertas', icon: '🔔', ready: true },
  // Fica ao lado de Alertas porque responde a mesma pergunta — "como o sistema
  // me avisa?" —, e não junto de Backup, que é manutenção.
  { id: 'whatsapp', label: 'Resumo no WhatsApp', icon: '✆', ready: true },
  { id: 'usuarios', label: 'Usuários', icon: '☻', ready: true },
  { id: 'auditoria', label: 'Auditoria', icon: '⎔', ready: true },
  { id: 'backup', label: 'Backup', icon: '⛁', ready: true },
  { id: 'atualizacoes', label: 'Atualizações', icon: '⇧', ready: true },
];

export function Shell() {
  const { user, logout } = useAuth();
  const [view, setView] = useState<View>('dashboard');
  const [cardInvoice, setCardInvoice] = useState<number | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);

  const entities = user?.entities ?? [];

  return (
    <div className="min-h-screen">
      {/* ---------- Barra superior ---------- */}
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur dark:border-slate-800 dark:bg-slate-900/90">
        <div className="flex h-14 items-center gap-3 px-4">
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 lg:hidden dark:hover:bg-slate-800"
            aria-label="Alternar menu"
          >
            ☰
          </button>

          <span className="hidden font-semibold tracking-tight sm:block">Sistema Financeiro</span>

          <EntitySelector entities={entities} selected={selected} onChange={setSelected} />
          <PeriodSelector />

          <div className="ml-auto flex items-center gap-2">
            {/* A hora do SERVIDOR, não a deste computador: é ela que decide
                backup, alertas e o resumo no WhatsApp. */}
            <ServerClock />
            <ThemeToggle />
            <NotificationBell onOpenAll={() => setView('alertas')} />
            <span className="hidden text-sm text-slate-500 sm:block dark:text-slate-400">
              {user?.name}
            </span>
            <button
              onClick={() => void logout()}
              className="rounded-lg px-3 py-1.5 text-sm text-slate-600 transition hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Sair
            </button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* ---------- Menu lateral ---------- */}
        <nav
          className={`${menuOpen ? 'block' : 'hidden'} w-56 shrink-0 border-r border-slate-200 bg-white p-3 lg:block dark:border-slate-800 dark:bg-slate-900`}
        >
          {MENU.map((item) => (
            <button
              key={item.id}
              onClick={() => item.ready && setView(item.id)}
              disabled={!item.ready}
              className={`mb-1 flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition ${
                view === item.id
                  ? 'bg-indigo-50 font-medium text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300'
                  : item.ready
                    ? 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                    : 'cursor-not-allowed text-slate-400 dark:text-slate-600'
              }`}
            >
              <span className="w-4 text-center opacity-70">{item.icon}</span>
              {item.label}
              {!item.ready && <span className="ml-auto text-[10px] uppercase tracking-wide">breve</span>}
            </button>
          ))}
        </nav>

        {/* ---------- Conteudo ---------- */}
        <main className="min-w-0 flex-1 p-5">
          {view === 'dashboard' && <DashboardPage selectedIds={selected} entities={entities} />}
          {view === 'lancamentos' && (
            <TransactionsPage
              selectedIds={selected}
              onOpenAccounts={() => setView('contas')}
              onOpenCards={(_cardId, invoiceId) => {
                setCardInvoice(invoiceId);
                setView('cartoes');
              }}
              onOpenDebts={() => setView('dividas')}
            />
          )}
          {view === 'contas' && (
            <AccountsPage
              selectedIds={selected}
              onOpenTransfers={() => setView('transferencias')}
            />
          )}
          {view === 'cartoes' && (
            <CardsPage selectedIds={selected} initialInvoiceId={cardInvoice} />
          )}
          {view === 'transferencias' && <TransfersPage selectedIds={selected} />}
          {view === 'recorrencias' && <RecurrencesPage selectedIds={selected} />}
          {view === 'pagar' && (
            <ObligationsPage
              kind="payables"
              selectedIds={selected}
              onOpenAccounts={() => setView('contas')}
              onOpenDebts={() => setView('dividas')}
              onOpenCards={() => setView('cartoes')}
            />
          )}
          {view === 'receber' && (
            <ObligationsPage
              kind="receivables"
              selectedIds={selected}
              onOpenAccounts={() => setView('contas')}
            />
          )}
          {view === 'contatos' && <PartiesPage selectedIds={selected} />}
          {view === 'relatorios' && <ReportsPage selectedIds={selected} />}
          {view === 'previsao' && <ForecastPage selectedIds={selected} />}
          {view === 'dividas' && <DebtsPage selectedIds={selected} />}
          {view === 'orcamento' && <BudgetPage selectedIds={selected} />}
          {view === 'categorias' && <CategoriesPage selectedIds={selected} />}
          {view === 'entidades' && <EntitiesPage selectedIds={selected} />}
          {view === 'contratos' && <ContractsPage selectedIds={selected} />}
          {view === 'alertas' && <AlertsPage selectedIds={selected} />}
          {/* A configuração é da organização, e o recorte por entidade acontece
              por destinatário — não recebe seletor. */}
          {view === 'whatsapp' && <WhatsAppPage />}
          {/* Usuarios e escopo de instalacao, nao de entidade. */}
          {view === 'usuarios' && <UsersPage />}
          {view === 'auditoria' && <AuditPage selectedIds={selected} />}
          {view === 'compartilhadas' && <SharingPage selectedIds={selected} />}
          {/* Backup e escopo de instalacao, nao de entidade: nao recebe seletor. */}
          {view === 'backup' && <BackupPage />}
          {/* Versao e atualizacao sao escopo de instalacao, nao de entidade. */}
          {view === 'atualizacoes' && <UpdatesPage />}
        </main>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// Seletor de entidade
// -------------------------------------------------------------------

function EntitySelector({
  entities,
  selected,
  onChange,
}: {
  entities: Entity[];
  selected: number[];
  onChange: (ids: number[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const box = useDismiss<HTMLDivElement>(open, () => setOpen(false));

  /**
   * O rotulo diz QUANTAS entidades existem quando nenhuma esta filtrada.
   *
   * "Consolidado (todas)" sozinho nao deixa claro que ha uma lista atras do
   * botao, nem quantas entidades o "todas" abrange — quem acabou de cadastrar
   * duas fica sem saber se elas entraram.
   */
  const label =
    selected.length === 0
      ? entities.length > 0
        ? `Consolidado · ${entities.length} entidade${entities.length > 1 ? 's' : ''}`
        : 'Nenhuma entidade'
      : selected.length === 1
        ? (entities.find((e) => e.id === selected[0])?.name ?? '—')
        : `${selected.length} de ${entities.length} entidades`;

  function toggle(id: number) {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  }

  return (
    <div className="relative" ref={box}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium transition hover:border-slate-400 dark:border-slate-700 dark:bg-slate-950"
      >
        <span className="max-w-[10rem] truncate">{label}</span>
        <span className="text-xs opacity-50">▾</span>
      </button>

      {open && (
        <div className="absolute left-0 top-full z-20 mt-1.5 w-64 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg dark:border-slate-800 dark:bg-slate-900">
          <button
            onClick={() => {
              onChange([]);
              setOpen(false);
            }}
            className={`mb-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm ${
              selected.length === 0
                ? 'bg-indigo-50 font-medium text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300'
                : 'hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
          >
            🌐 Consolidado (todas)
          </button>

          <div className="my-1 border-t border-slate-100 dark:border-slate-800" />

          {entities.map((e) => (
            <label
              key={e.id}
              className="flex cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <input
                type="checkbox"
                checked={selected.includes(e.id)}
                onChange={() => toggle(e.id)}
                className="rounded border-slate-300 text-indigo-600 dark:border-slate-600 focus:ring-indigo-500"
              />
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ background: e.color ?? '#94a3b8' }}
              />
              <span className="min-w-0 flex-1 truncate">{e.name}</span>
              <span className="text-[10px] uppercase tracking-wide text-slate-400">
                {e.type === 'empresa' ? 'PJ' : e.type === 'pessoa_fisica' ? 'PF' : ''}
              </span>
            </label>
          ))}

          {/* Sem entidade, o sistema inteiro fica vazio — e a tela nao
              explicaria por que. Aqui o caminho fica dito. */}
          {entities.length === 0 && (
            <p className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">
              Nenhuma entidade cadastrada. Crie a primeira em <strong>Entidades</strong>, no menu
              lateral — você recebe acesso a ela automaticamente.
            </p>
          )}

          {selected.length > 1 && (
            <p className="mt-1.5 border-t border-slate-100 px-3 pt-2 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
              Operações entre estas entidades serão eliminadas para evitar contagem dupla.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
